import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import set from 'lodash/set'
import get from 'lodash/get'

const DEFAULT_VALUE = Object.freeze({})
const VariableContext = createContext({
  variables: {},
  setVariable: () => {},
  setVariables: () => {}
})

export const useVariables = () => {
  return useContext(VariableContext)
}

export const useVariableManager = () => {
  const contextValue = useVariables()
  const prevVariables = contextValue?.variables
  const [variables, setVariables] = useState(DEFAULT_VALUE)
  const setVariable = useCallback((key, value) => {
    setVariables(prev => {
      const newValue = { ...(prev || {}) }
      set(newValue, key, value)
      return newValue
    })
  }, [setVariables])

  // Merge a dictionary of variables (used by VariableDefaults)
  const mergeVariables = useCallback((obj) => {
    const entries = obj ? Object.entries(obj) : []
    if (!entries.length) return
    setVariables(prev => {
      const newValue = { ...(prev || {}) }
      entries.forEach(([key, value]) => {
        set(newValue, key, value)
      })
      return newValue
    })
  }, [setVariables])

  const getVariable = useCallback((key, defaultValue) => {
    return get(variables, key, defaultValue)
  }, [variables])

  return useMemo(() => ({
    variables: {
      ...(prevVariables || {}),
      ...(variables || {})
    },
    setVariable,
    setVariables,
    getVariable,
    mergeVariables
  }), [variables, prevVariables, setVariables, setVariable, getVariable, mergeVariables])
}

function VariableProvider ({ children }) {
  const providerValue = useVariableManager()

  return (
    <VariableContext.Provider value={providerValue}>
      {children}
    </VariableContext.Provider>
  )
}

VariableProvider.propTypes = {
  children: PropTypes.node
}

VariableProvider.defaultProps = {
}

export default VariableProvider
